import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Grid, TextField } from '@mui/material';

const PREFIX = 'WarningDialog';

const classes = {
  actionContainer: `${PREFIX}-actionContainer`,
  notes: `${PREFIX}-notes`,
  title: `${PREFIX}-title`,
  dialogText: `${PREFIX}-dialogText`,
  helperText: `${PREFIX}-helperText`,
  paperContainer: `${PREFIX}-paperContainer`,
  previewButtonLabel: `${PREFIX}-previewButtonLabel`,

};
const StyledDialog = styled(Dialog)(() => ({
  [`& .${classes.actionContainer}`]: {
    padding: '0px 24px 24px',
    width: '100%',
  },

  [`& .${classes.notes}`]: {
    width: '510px',
    padding: '10px 25px 22px 25px',
  },

  [`& .${classes.title}`]: {
    marginTop: '15px',
  },

  [`& .${classes.dialogText}`]: {
    paddingTop: '0px',
  },

  [`& .${classes.helperText}`]: {
    margin: '6px 0px 0px 4px !important',
  },
  [`& .${classes.paperContainer}`]: {
    width: '484px',
  },
  [`& .${classes.previewButtonLabel}`]: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
  },
}));

const WarningDialog = ({
  open,
  onClose,
  onSubmit,
  onPreviewClick,
  warningMessage,
  postiveButtonText,
  cancelButtonText,
  title,
  showCancelButton,
  isInputVisible,
  setInput,
  inputValue,
  errorMsg,
  inputLabel,
  showPreviewButton,
  previewButtonText,
}) => {
  const [isError, setError] = useState(false);

  return (
    <StyledDialog
      open={open}
      keepMounted
      classes={{
        paper: classes.paperContainer,
      }}
      aria-labelledby="warning-dialog"
      data-testid="warning-dialog"
      PaperProps={{ variant: 'outlined' }}
    >
      <DialogTitle
        data-testid="alert-dialog-title"
        className={isInputVisible ? classes.title : ''}
      >
        <span style={{ fontSize: '20px', color: 'white' }}>{title}</span>
      </DialogTitle>
      <DialogContent className={isInputVisible ? classes.dialogText : ''}>
        <DialogContentText>{warningMessage}</DialogContentText>
      </DialogContent>
      {isInputVisible && (
        <Grid container xs={12} justifyContent="flex-end">
          <Grid classes={{ root: classes.notes }}>
            <TextField
              fullWidth
              id="notes"
              size="small"
              variant="filled"
              value={inputValue}
              data-testid="dialog-container"
              InputProps={{
                disableUnderline: true,
                inputProps: {
                  maxlength: 1000,
                },
              }}
              error={isError}
              helperText={isError ? errorMsg : ''}
              FormHelperTextProps={{
                classes: {
                  root: classes.helperText,
                },
              }}
              label={inputLabel}
              multiline
              rows={8}
              name="notes"
              onChange={(e) => {
                if (!e.target.value.trim()) setError(true);
                else setError(false);
                setInput(e.target.value);
              }}
              type="text"
            />
          </Grid>
        </Grid>
      )}

      <DialogActions classes={{ root: classes.actionContainer }}>
        <Grid container justifyContent="space-between">
          <Grid item>
            {
              showPreviewButton && (
                <Button
                  onClick={() => onPreviewClick()}
                  variant="text"
                  color="primary"
                  classes={{
                    root: classes.previewButtonLabel,
                  }}
                >
                  {previewButtonText}
                </Button>
              )
            }
          </Grid>
          <Grid item justifyContent="flex-end">
            {showCancelButton && (
              <Button onClick={() => onClose()} variant="outlined" color="primary">
                {cancelButtonText}
              </Button>
            )}
            <Button
              onClick={() => onSubmit()}
              disabled={isInputVisible ? inputValue.trim() === '' : false}
              variant="contained"
              color="primary"
            >
              {postiveButtonText}
            </Button>

          </Grid>

        </Grid>

      </DialogActions>
    </StyledDialog>
  );
};

WarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPreviewClick: PropTypes.func,
  postiveButtonText: PropTypes.string,
  warningMessage: PropTypes.string,
  cancelButtonText: PropTypes.string,
  title: PropTypes.string,
  showCancelButton: PropTypes.bool,
  isInputVisible: PropTypes.bool,
  setInput: PropTypes.func,
  inputValue: PropTypes.string,
  errorMsg: PropTypes.string,
  inputLabel: PropTypes.string,
  showPreviewButton: PropTypes.bool,
  previewButtonText: PropTypes.string,

};

WarningDialog.defaultProps = {
  warningMessage: t`On cancel changes will be lost, are you sure you want to continue?`,
  postiveButtonText: t`YES`,
  cancelButtonText: t`NO`,
  showPreviewButton: false,
  previewButtonText: t`Preview`,
  title: '',
  showCancelButton: true,
  inputValue: '',
  errorMsg: '',
  inputLabel: '',
  setInput: null,
  isInputVisible: false,
  onPreviewClick: () => {},
};

export default WarningDialog;
